<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active"> List of Alerts </span>
      <a-button size="default" class="a-header--button success-btn" @click="showDownloadPopup"> <a-icon type="cloud-download" /> Download </a-button>
    </div>
    <div v-if="isLoading" class="d-flex justify-content-center align-items-center pt-35-px">
      <Spinner />
    </div>
    <transition name="slide-up">
      <div v-if="!isLoading" class="a-filter">
        <a-row class="full-w" type="flex" justify="start" align="middle" :gutter="24">
          <a-col :span="6">
            <div class="full-w a-input-group" :class="{ active: !!filterParams.search }">
              <a-button size="large">
                <a-icon type="search" />
              </a-button>
              <a-input class="custom-prefix" placeholder="Search" :default-value="filterParams.search" @change="onSearch">
                <a-icon v-show="isSearching" slot="suffix" class="custom-suffix" type="loading" spin />
              </a-input>
            </div>
          </a-col>
          <a-col>
            <a-checkbox v-model="filterParams.exactMatch" @change="onChangeExactMatch">
              Exact match
            </a-checkbox>
          </a-col>
          <a-col :span="8">
            <div class="full-w a-input-group" :class="{ active: !!filterParams.fromDate && !!filterParams.toDate }">
              <a-button size="large">
                <a-icon type="filter" />
              </a-button>
              <a-range-picker :ranges="RANGE" :placeholder="['From', 'To']" :format="DATE_FORMAT.DDMMMYYYY" @change="onChangeDate" />
            </div>
          </a-col>
          <a-col v-if="showCombinedFilter" :span="6">
            <div class="full-w a-select-group" :class="{ active: hasCombinedFilterActive }">
              <a-button size="large">
                <a-icon type="filter" />
              </a-button>
              <a-select
                v-model="filterParams.combinedFilter"
                mode="multiple"
                :size="'large'"
                :placeholder="combinedFilterPlaceholder"
                :maxTagCount="2"
                :maxTagTextLength="18"
                :filterOption="filterCombinedOption"
                @search="onSearchCombinedFilter"
                @change="onChangeCombinedFilter"
              >
                <a-select-opt-group v-if="featureAccess && featureAccess['alert-triage']" label="Severity">
                  <a-select-option v-for="option in severityOptions" :key="'severity:' + option.value" :value="'severity:' + option.value">
                    {{ option.label }}
                  </a-select-option>
                </a-select-opt-group>
                <a-select-opt-group v-if="featureAccess && featureAccess['tags']" label="Tags">
                  <a-select-option v-for="tag in availableTags" :key="'tag:' + tag.id" :value="'tag:' + tag.id">
                    {{ tag.name }}
                  </a-select-option>
                </a-select-opt-group>
                <a-select-opt-group v-if="isStatusCodeMonitoring" label="HTTP Status">
                  <a-select-option v-for="option in httpStatusSelectOptions" :key="'status:' + option.value" :value="'status:' + option.value">
                    {{ option.label }}
                  </a-select-option>
                </a-select-opt-group>
              </a-select>
            </div>
          </a-col>
        </a-row>
      </div>
    </transition>
    <div v-resize="elSizeChange" class="a-body flex-grow-1 overflow-y-hidden">
      <transition name="slide-up">
        <template v-if="!isLoading">
          <PaginationTable :clientPaging="false" :pagination="pagin" :columns="filteredColumns" :dataSource="filterData" :tableSize="tableSize">
            <template slot="webpageName" slot-scope="{ record }">
              <a-tooltip :title="record.webpageName" placement="topLeft">
                <span class="webpage-name">{{ record.webpageName }}</span>
              </a-tooltip>
            </template>
            <template slot="webpageUrl" slot-scope="{ record }">
              <div>
                <a-tooltip :title="record.webpageUrl" placement="topLeft">
                  <a class="webpab-ext-link" :href="record.webpageUrl" target="_blank" rel="noopener noreferrer">
                    {{ record.webpageUrl }}
                  </a>
                </a-tooltip>
              </div>
            </template>
            <template slot="aggregation" slot-scope="{ record }">
              <div v-if="record.aggregation" class="al-agg">
                <router-link
                  class="al-agg__event"
                  :to="'/change-events/detail/' + record.aggregation.change_event_id"
                >{{ record.aggregation.change_type || 'Aggregated alert' }}</router-link>
                <span class="al-agg__meta">
                  with {{ record.aggregation.alert_count }} alerts on
                  {{ record.aggregation.page_count }} pages
                </span>
                <router-link
                  v-if="record.aggregation.site_incident_id"
                  class="al-agg__site"
                  :to="'/change-events/site-incidents/' + record.aggregation.site_incident_id"
                >{{ record.aggregation.site_incident_category }}</router-link>
              </div>
              <span v-else class="al-agg__none">-</span>
            </template>
            <template slot="incidentId" slot-scope="{ record }">
              <div>
                <router-link :to="'/incident/detail/' + record.id">
                  {{ record.id }}
                </router-link>
              </div>
            </template>
            <template slot="detectedAt" slot-scope="{ record }">
              <div>
                <p class="mb-0">
                  <time>{{ record.detectedAt | dateString }}</time>
                  <br />
                  <time>{{ record.detectedAt | timeString }}</time>
                </p>
              </div>
            </template>
            <template slot="httpStatus" slot-scope="{ record }">
              <a-tag v-if="record.httpStatusLabel" :color="getHttpStatusColor(record.httpStatusCode)">
                {{ record.httpStatusLabel }}
              </a-tag>
              <span v-else>-</span>
            </template>
            <template slot="tags" slot-scope="{ record }">
              <div>
                <a-tag v-for="tag in record.tags" :key="tag">{{ tag }}</a-tag>
              </div>
            </template>
            <span
              v-if="featureAccess && featureAccess['alert-triage']"
              slot="severity"
              slot-scope="{ record }"
            >
              <a-tag class="severity" :style="severityTagStyle(record.severity)">
                {{ record.severity }}
              </a-tag>
            </span>
            <template slot="operation" slot-scope="{ record }">
              <a-dropdown placement="topLeft">
                <a-icon class="operation" type="ellipsis" />
                <a-menu slot="overlay" class="operation-menu">
                  <a-menu-item>
                    <a-icon type="eye" />
                    <router-link class="nav-link-detail" :to="'/incident/detail/' + record.id"> View Detail </router-link>
                  </a-menu-item>
                </a-menu>
              </a-dropdown>
            </template>
          </PaginationTable>
        </template>
      </transition>
    </div>
    <div class="a-footer">
      <PaginationBar :current="pagin.current" :pageSize.sync="pagin.pageSize" :total="pagin.total" @change="paginChange" />
    </div>
    <a-modal v-model="isShowDownloadPopup" title="Download List of Alerts" :footer="null" :closable="true" :maskClosable="false" :keyboard="false">
      <a-form :form="downloadForm" @submit="handleDownloadSubmit">
        <a-row :gutter="24">
          <a-col :span="24">
            <a-form-item label="Date Range">
              <a-range-picker
                :ranges="RANGE"
                :placeholder="['From', 'To']"
                :format="DATE_FORMAT.DDMMMYYYY"
                v-decorator="[
                  'date_range',
                  {
                    rules: [{ required: true, message: 'Please select a date range' }],
                    initialValue: RANGE['Last 3 Months'],
                  },
                ]"
              />
              <p v-if="allowedPeriodStartDate">Valid selection period: {{ allowedPeriodStartDate | dateString }} to {{ allowedPeriodEndDate | dateString }}</p>
              <p><a-icon type="info-circle" /> Note that very large usage reports may be truncated.</p>
            </a-form-item>
          </a-col>
        </a-row>
        <a-row>
          <a-col :span="16">
            <p v-if="downloadProcess.total > 0 && downloadProcess.loaded >= downloadProcess.total">Processing file download...</p>
            <p v-else-if="downloadProcess.total > 0" class="ant-form-item">Processing {{ downloadProcess.loaded }} of {{ downloadProcess.total }} records...</p>
          </a-col>
          <a-col :span="8" :style="{ textAlign: 'right' }">
            <a-button type="primary" html-type="submit" :loading="isDisableDownloadFile"> Download </a-button>
          </a-col>
        </a-row>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import { PAGE_OPTIONS } from '@constants/pageOptions';
import { indexItemTable } from '@shared/helpers/utils';
import { IncidentListColumn } from '../columnsTable/IncidentListColumn';
import IncidentService from '../services/Incident.service';
import { DATE_FORMAT } from '@constants/dateFormat';
import TagsService from '../../Tags/services/tags.service';
import moment from 'moment-timezone';
import { severityColor } from '@modules/ChangeEvents/consts/ChangeEvents.consts';
import { formatHttpStatus, getHttpStatusColor } from '@shared/helpers/http-status';

export const RANGE = {
  Today: [moment().set({ hour: 0, minute: 0, second: 0, millisecond: 1 }), moment().set({ hour: 23, minute: 59, second: 59, millisecond: 59 })],
  Yesterday: [moment().subtract(1, 'days').set({ hour: 0, minute: 0, second: 0, millisecond: 1 }), moment().subtract(1, 'days').set({ hour: 23, minute: 59, second: 59, millisecond: 59 })],
  'This Week': [moment().set({ hour: 0, minute: 0, second: 0, millisecond: 1 }), moment().set({ hour: 23, minute: 59, second: 59, millisecond: 59 })],
  'Last Week': [moment().subtract(1, 'week').startOf('week'), moment().subtract(1, 'week').endOf('week')],
  'This Month': [moment().startOf('month'), moment().endOf('month')],
  'Last Month': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
  'Last 3 Months': [moment().subtract(3, 'month').startOf('month'), moment().endOf('month')],
};

export default {
  name: 'IncidentList',
  data() {
    var pageSize = Number(window.localStorage.getItem('pageSize'));
    if (!pageSize) pageSize = 5;
    const tagId = this.tagIdFromQuery();

    return {
      RANGE,
      PAGE_OPTIONS,
      DATE_FORMAT,
      IncidentListColumn,
        data: [],
      filterData: [],
      // Staleness guard for loadAggregations: bumped per request, compared
      // before the grouping is written onto the rows.
      aggToken: 0,
      filterParams: {
        // Prefill from a ?search= query param (e.g. deep-linked from a dashboard
        // widget) so the alerts list opens already filtered to that URL.
        // Normalised like `onSearch` so both entry points send the same term.
        search:
          this.$route && this.$route.query && this.$route.query.search
            ? String(this.$route.query.search).trim().toLowerCase()
            : '',
        exactMatch: false,
        fromDate: null,
        toDate: null,
        // 'severity:X' / 'tag:X' — must match the select's option values.
        combinedFilter: tagId ? ['tag:' + tagId] : [],
      },
      severityOptions: [
        { value: 'NA', label: 'N/A' },
        { value: 'LOW', label: 'LOW' },
        { value: 'MEDIUM', label: 'MEDIUM' },
        { value: 'HIGH', label: 'HIGH' },
      ],
      httpStatusSearch: '',
      pagin: {
        current: 1,
        pageSize: pageSize,
        total: 0,
      },
      isSearching: false,
      isLoading: true,
      tableSize: {
        height: '',
        width: '',
      },
      allowedPeriodStartDate: null,
      allowedPeriodEndDate: null,
      isShowDownloadPopup: false,
      isDisableDownloadFile: false,
      downloadForm: this.$form.createForm(this, { name: 'download_form' }),
      downloadProcess: {
        loaded: 0,
        total: 0
      },
        availableTags: [],
    }
  },
  computed: {
    featureAccess() {
      return this.$store.getters.featureAccess;
    },
    isAlertTriage() {
      return Boolean(this.featureAccess['alert-triage']);
    },
    isStatusCodeMonitoring() {
      return this.$store.getters.hasFeature('status-code-monitoring');
    },
    filteredColumns() {
      return this.IncidentListColumn.filter(column => {
        if (column.title === 'Severity' && !this.isAlertTriage) {
          return false;
        }
        if (column.title === 'HTTP Status' && !this.isStatusCodeMonitoring) {
          return false;
        }
        if (column.title === 'Tags' && !(this.featureAccess && this.featureAccess['tags'])) {
          return false;
        }
        if (column.title === 'Grouped' && !this.hasChangeEvents) {
          return false;
        }
        return true;
      });
    },
    /** Whether Change Events is switched on for this customer. */
    hasChangeEvents() {
      return !!(this.featureAccess && this.featureAccess['change-events']);
    },
    custTimezone() {
      return this.$store.state.user.data.timezone;
    },
    showCombinedFilter() {
      return (this.featureAccess && this.featureAccess['tags'])
        || (this.featureAccess && this.featureAccess['alert-triage'])
        || this.isStatusCodeMonitoring;
    },
    combinedFilterPlaceholder() {
      const parts = [];
      if (this.featureAccess && this.featureAccess['alert-triage']) {
        parts.push('Severity');
      }
      if (this.featureAccess && this.featureAccess['tags']) {
        parts.push('Tags');
      }
      if (this.isStatusCodeMonitoring) {
        parts.push('HTTP Status');
      }
      if (parts.length === 0) {
        return 'Filter';
      }
      return 'Filter by ' + parts.join(', ') + '...';
    },
    // Check if combined filter has any active selections
    hasCombinedFilterActive() {
      return Array.isArray(this.filterParams.combinedFilter) && this.filterParams.combinedFilter.length > 0;
    },
    // Parse combined filter into separate arrays for API calls
    parsedCombinedFilter() {
      const result = { severityLevels: [], tags: [], httpStatusCodes: [] };
      const combined = this.filterParams.combinedFilter || [];
      combined.forEach(val => {
        if (val.startsWith('severity:')) {
          result.severityLevels.push(val.replace('severity:', ''));
        } else if (val.startsWith('tag:')) {
          result.tags.push(val.replace('tag:', ''));
        } else if (val.startsWith('status:')) {
          result.httpStatusCodes.push(val.replace('status:', ''));
        }
      });
      return result;
    },
    httpStatusSelectOptions() {
      const options = [
        { value: '0', label: 'No HTTP response' },
        { value: 'null', label: 'Unknown' }
      ];
      const seen = { '0': true, null: true };
      const addCode = code => {
        if (!this.isValidHttpStatusNumber(code) || seen[code]) return;
        seen[code] = true;
        options.unshift({ value: String(code), label: String(code) });
      };
      (this.filterParams.combinedFilter || []).forEach(val => {
        if (typeof val === 'string' && val.startsWith('status:')) {
          addCode(val.slice('status:'.length));
        }
      });
      addCode(String(this.httpStatusSearch || '').trim());
      return options;
    }
  },
  mounted() {
    this.fetchList();
  },
  beforeDestroy() {},
  methods: {
    getHttpStatusColor,
    severityTagStyle(severity) {
      const color = severityColor(severity);
      return {
        backgroundColor: color,
        borderColor: color
      };
    },
    // Only honour ?tag= where the account can see and clear the resulting chip.
    tagIdFromQuery() {
      if (!this.$store.getters.hasFeature('tags')) return null;
      const query = (this.$route && this.$route.query) || {};
      // Repeated ?tag= params arrive as an array.
      const raw = Array.isArray(query.tag) ? query.tag[0] : query.tag;
      return /^\d+$/.test(String(raw == null ? '' : raw)) ? String(raw) : null;
    },
    onSearch: debounce(function (inputChangeEvent) {
      const inputSearchValue = inputChangeEvent.target.value;
      this.filterParams.search = inputSearchValue ? String(inputSearchValue).trim().toLowerCase() : '';
      this.fetchList();
    }, 800),
    /**
     * Adds the grouping to the rows already on screen.
     *
     * Deliberately quiet: this is extra information beside an alert, not the
     * alert itself, so a failure or a switched-off feature simply leaves the
     * column empty rather than showing the user an error about a list that
     * loaded perfectly well.
     */
    async loadAggregations() {
      if (!this.hasChangeEvents || !this.data.length) return;
      const ids = this.data.map(row => row.id).filter(Boolean);
      if (!ids.length) return;
      // Only the newest request may write. fetchList does not await this, so
      // a slow answer for an earlier page could land after a newer page was
      // drawn, look every new row up in the old map, and blank the column.
      const token = ++this.aggToken;
      try {
        const grouping = await IncidentService.fetchAggregations(ids);
        if (token !== this.aggToken) return;
        if (!grouping || typeof grouping !== 'object') return;
        // Rebuilt rather than mutated so the table redraws.
        this.data = this.data.map(row => ({ ...row, aggregation: grouping[row.id] || null }));
        this.filterData = [...this.data];
      } catch (err) {
        // Nothing is shown to the user: the list is still correct without it.
        // It IS logged, because a silent catch here hid a real bug once - the
        // ids were never being sent and the column simply stayed empty.
        // eslint-disable-next-line no-console
        console.warn('[ChangeEvents] could not load the grouping for this page', err);
      }
    },
    async fetchList() {
      try {
        this.isSearching = true;
        const { tags, severityLevels, httpStatusCodes } = this.parsedCombinedFilter;
        const [responseData, tagsData] = await Promise.all([
          IncidentService.fetchList(
            this.pagin.current,
            this.pagin.pageSize,
            this.filterParams.search,
            this.filterParams.fromDate,
            this.filterParams.toDate,
            null,
            tags,
            severityLevels,
            httpStatusCodes,
            this.filterParams.exactMatch
          ),
          TagsService.getList((this.$store.state.user.data || {}).id)
        ]);
        const { data, total } = responseData;
        this.availableTags = tagsData;
        this.data = (data || []).map((item, index) => ({
          index: indexItemTable(this.pagin.current, this.pagin.pageSize, index),
          id: item.id,
          webpageName: (item.url && item.url.name) || '',
          webpageUrl: (item.url && item.url.url) || '',
          detectedAt: item.time_detected,
          httpStatusCode: item.http_status_code,
          httpStatusLabel: formatHttpStatus(item.http_status_code),
          severity: (item.triage_result && item.triage_result.severity_level) || 'N/A',
          tags: Array.isArray(item && item.url && item.url.tags) ? item.url.tags.map(t => t && t.name).filter(Boolean) : []
        }));
        this.filterData = [...this.data];
        this.pagin.total = total;
        this.isSearching = false;
        // Drawn first, grouping added after. One request for the whole page,
        // and a failure leaves the list exactly as it is.
        this.loadAggregations();
      } catch(err) {
        this.$showMultiErrorMessage(err.messages);
      } finally {
        if (this.isLoading) {
          setTimeout(() => {
            this.isLoading = false;
          }, 300);
        }
      }
    },
    isValidHttpStatusNumber(value) {
      const text = String(value == null ? '' : value).trim();
      if (!/^(0|[1-9][0-9]{0,4})$/.test(text)) return false;
      const code = Number(text);
      return code >= 0 && code <= 65535;
    },
    onSearchCombinedFilter(value) {
      this.httpStatusSearch = value || '';
    },
    filterCombinedOption(input, option) {
      const query = String(input || '').trim().toLowerCase();
      if (!query) return true;
      const props = option.componentOptions && option.componentOptions.propsData;
      const value = props && props.value != null ? String(props.value).toLowerCase() : '';
      const children = option.componentOptions && option.componentOptions.children;
      const label = children && children[0] && children[0].text ? String(children[0].text).trim().toLowerCase() : '';
      if (label.indexOf(query) >= 0) return true;
      if (value.indexOf('status:') === 0) {
        return value.slice('status:'.length).indexOf(query) === 0;
      }
      return false;
    },
    onChangeCombinedFilter(selectedValues) {
      this.filterParams.combinedFilter = Array.isArray(selectedValues) ? selectedValues : [];
      this.httpStatusSearch = '';
      this.pagin.current = 1;
      this.fetchList();
    },
    onChangeExactMatch() {
      this.pagin.current = 1;
      this.fetchList();
    },
    async showDownloadPopup() {
      this.isShowDownloadPopup = true;
      let allowedPeriod = await IncidentService.fetchAllowedPeriod();
      this.allowedPeriodStartDate = moment(allowedPeriod.start_date).toISOString();
      // Push the allowed period end date to the end of the month in the UI
      this.allowedPeriodEndDate = moment(allowedPeriod.end_date).endOf('month').toISOString();
    },
    getFilteredUTCStartDate(start, format) {
      // moment.tz ensures that the date will be interpreted according to the user's selected timezonee
      return moment.tz(start, format, this.custTimezone).utc().format("YYYY-MM-DD HH:mm:ss");
    },
    getFilteredUTCEndDate(end, format) {
      return moment.tz(end, format, this.custTimezone).endOf('day').utc().format("YYYY-MM-DD HH:mm:ss");
    },
    onChangeDate(dates, dateStrings) {
      // convert time to utc
      this.filterParams.fromDate = dateStrings[0] ? this.getFilteredUTCStartDate(dateStrings[0], "DD MMM YYYY") : null;
      this.filterParams.toDate = dateStrings[1] ? this.getFilteredUTCEndDate(dateStrings[1], "DD MMM YYYY") : null;
      this.fetchList();
    },
    handleDownloadSubmit(e) {
      // TODO
      e.preventDefault();
      this.downloadForm.validateFields(async (err, values) => {
        if (!err) {
          console.log('Received values of form: ', values);
          try {
            this.isDisableDownloadFile = true;
            const dateStrings = values.date_range;

            // convert to utc
            const startDate = dateStrings[0] ? this.getFilteredUTCStartDate(dateStrings[0].format("YYYY-MM-DD"), "YYYY-MM-DD") : null;
            const endDate = dateStrings[1] ? this.getFilteredUTCEndDate(dateStrings[1].format("YYYY-MM-DD"), "YYYY-MM-DD") : null;

            // retrieve offset in ISO string format to be used as csv column header
            const offset = moment.tz(new Date(), this.custTimezone).format('Z');

            let link = document.createElement('a');
            let csvData = [];
            let csvHeader = ['No.', 'Webpage Name', 'Webpage URL', 'Time Detected (UTC)', `Time Detected (${this.custTimezone} ${offset})`];
            if (this.isStatusCodeMonitoring) {
              csvHeader.push('HTTP Status');
            }
            csvHeader.push('Alert ID', 'Comment');
            csvData.push(csvHeader);

            let pageNum = 1;
            let rowNum = 1;
            let response = {};
            do {
              if (!this.isShowDownloadPopup) {
                // If user closed the download popup, we just reset everything and stop processing
                this.downloadProcess.loaded = 0;
                this.downloadProcess.total = 0;
                return;
              }

              response = await IncidentService.fetchExportList(pageNum, 1000, '', startDate, endDate);
              const downloadData = response['data'];
              for (let i = 0; i < downloadData.length; i++) {
                // time retrieved comes back as utc
                // convert this date time to a moment object
                // convert UTC moment object to the user's selected time zone
                const momentInUTC = moment.tz(downloadData[i].time_detected, "UTC");
                const formattedMomentInUTC = momentInUTC.format("DD/MM/YYYY HH:mm:ss");
                const convertedDateTime = momentInUTC.tz(this.custTimezone).format("DD/MM/YYYY HH:mm:ss");
                const csvRow = [rowNum, downloadData[i].url.name, downloadData[i].url.url, formattedMomentInUTC, convertedDateTime];
                if (this.isStatusCodeMonitoring) {
                  csvRow.push(formatHttpStatus(downloadData[i].http_status_code) || '');
                }
                csvRow.push(downloadData[i].id, downloadData[i].comment);
                csvData.push(csvRow);
                rowNum++;
              }
              this.downloadProcess.loaded = response['to'];
              this.downloadProcess.total = response['total'];
              pageNum++;
            } while(response['to'] < response['total']);
            // Format data into stringresponseData
            let csvDataStringRows = [];
            for (let row of csvData) {
              row = row.map(val => {
                if (val == null) {
                  return '""';
                } else {
                  let valReplacedSpecialChars = val
                    .toString()
                    .replace(/[\r\n\t]/gim, '')
                    .replace('"', '""');
                  return '"' + valReplacedSpecialChars + '"';
                }
              });
              csvDataStringRows.push(row.join(','));
            }
            let csvDataString = csvDataStringRows.join('\n');

            link.href = window.URL.createObjectURL(new Blob([csvDataString], { type: 'text/csv;charset=utf-8;' }));
            link.setAttribute('download', 'alerts.csv');
            document.body.appendChild(link);
            link.click();

            this.downloadProcess.loaded = 0;
            this.downloadProcess.total = 0;

            this.$showSuccessMessage('Alert export completed');

          } catch (err) {
            this.$showMultiErrorMessage(err.messages);
          } finally {
            setTimeout(() => {
              this.isDisableDownloadFile = false;
            }, 2000);
          }
        }
      });
    },
    paginChange(pagin) {
      this.pagin = { ...pagin };
      this.fetchList();
    },
    elSizeChange(size) {
      this.tableSize = size;
    },
  },
};
</script>

<style lang="scss" scoped>
  // ---- the grouping of one alert, shown beside it in the list
  .al-agg {
    font-size: 12px;
    line-height: 1.45;

    &__event {
      font-weight: 600;
      display: block;
    }
    &__meta {
      color: #8c8c8c;
      display: block;
    }
    &__site {
      display: inline-block;
      margin-top: 2px;
      color: #ff4d4f;
      font-weight: 600;
    }
    &__none {
      color: #bfbfbf;
    }
  }

.time-text {
  margin-bottom: 0;
  white-space: nowrap;
}
.nav-link-detail {
  width: 100%;
  &:hover {
    text-decoration: underline;
  }
}
.nav-link-detail {
  display: inline-block;
}
.custom-suffix {
  font-size: 20px;
  color: #20a4b8;
}
.webpage-name {
  display: block;
  max-width: 230px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.webpab-ext-link {
  color: inherit;
  display: block;
  width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  &:hover {
    color: #20a4b8;
    text-decoration: underline;
  }
}

.severity.ant-tag {
  color:#ffffff;
  font-size: 10px;
}

</style>
